// Phone number utilities for US/Canada formats
// Supports formats: (XXX) XXX-XXXX, XXX-XXX-XXXX, XXX.XXX.XXXX

/**
 * Validates a phone number for US/Canada formats
 * @param phone - The phone number to validate
 * @returns boolean indicating if the phone number is valid
 */
export const validatePhoneNumber = (phone: string): boolean => {
  if (!phone || phone.trim() === '') return true; // Allow empty values
  
  // Remove all non-digit characters for validation
  const digitsOnly = phone.replace(/\D/g, '');
  
  // Must be exactly 10 digits (US/Canada without country code)
  // or 11 digits starting with 1 (with country code)
  if (digitsOnly.length === 10) {
    return true;
  }
  
  if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
    return true;
  }
  
  return false;
};

/**
 * Formats a phone number to (XXX) XXX-XXXX format
 * @param phone - The phone number to format
 * @returns Formatted phone number string
 */
export const formatPhoneNumber = (phone: string): string => {
  if (!phone) return '';
  
  // Remove all non-digit characters
  const digitsOnly = phone.replace(/\D/g, '');
  
  // Handle different lengths
  if (digitsOnly.length === 0) return '';
  
  // Remove leading 1 if present (country code)
  const phoneDigits = digitsOnly.startsWith('1') && digitsOnly.length === 11 
    ? digitsOnly.slice(1) 
    : digitsOnly;
  
  // Format as (XXX) XXX-XXXX
  if (phoneDigits.length >= 10) {
    return `(${phoneDigits.slice(0, 3)}) ${phoneDigits.slice(3, 6)}-${phoneDigits.slice(6, 10)}`;
  } else if (phoneDigits.length >= 6) {
    return `(${phoneDigits.slice(0, 3)}) ${phoneDigits.slice(3, 6)}-${phoneDigits.slice(6)}`;
  } else if (phoneDigits.length >= 3) {
    return `(${phoneDigits.slice(0, 3)}) ${phoneDigits.slice(3)}`;
  } else {
    return `(${phoneDigits}`;
  }
};

/**
 * Formats phone number as user types (for real-time input formatting)
 * @param value - Current input value
 * @param previousValue - Previous input value (to handle backspace properly)
 * @returns Formatted phone number
 */
export const formatPhoneInput = (value: string, previousValue: string = ''): string => {
  if (!value) return '';
  
  // Remove all non-digit characters
  const digitsOnly = value.replace(/\D/g, '');
  
  // Limit to 10 digits (without country code)
  const limitedDigits = digitsOnly.slice(0, 10);
  
  // If user is deleting and we're at a formatting character, help them delete the digit
  if (value.length < previousValue.length) {
    const prevDigits = previousValue.replace(/\D/g, '');
    if (prevDigits.length > limitedDigits.length) {
      // User deleted a digit, format normally
      return formatPhoneNumber(limitedDigits);
    }
  }
  
  return formatPhoneNumber(limitedDigits);
};

/**
 * Extracts clean digits from a formatted phone number
 * @param phone - Formatted phone number
 * @returns Clean digits string
 */
export const getPhoneDigits = (phone: string): string => {
  if (!phone) return '';
  return phone.replace(/\D/g, '');
};

/**
 * Checks if a phone number appears to be complete (10 digits)
 * @param phone - Phone number to check
 * @returns boolean indicating if phone appears complete
 */
export const isPhoneComplete = (phone: string): boolean => {
  const digits = getPhoneDigits(phone);
  return digits.length === 10;
};

/**
 * Gets phone number validation error message
 * @param phone - Phone number to validate
 * @returns Error message string or null if valid
 */
export const getPhoneValidationError = (phone: string): string | null => {
  if (!phone || phone.trim() === '') return null;
  
  const digits = getPhoneDigits(phone);
  
  if (digits.length === 0) return null;
  
  if (digits.length < 10) {
    return 'Phone number must be 10 digits';
  }
  
  if (digits.length > 11) {
    return 'Phone number is too long';
  }
  
  if (digits.length === 11 && !digits.startsWith('1')) {
    return 'Invalid country code';
  }
  
  return null;
};

/**
 * Creates a masked input handler for phone number fields
 * @param onChange - Original onChange handler
 * @returns Masked onChange handler
 */
export const createPhoneMaskHandler = (
  onChange: (value: string) => void
) => {
  let previousValue = '';
  
  return (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    const formattedValue = formatPhoneInput(inputValue, previousValue);
    
    // Update the input field value directly to maintain cursor position
    event.target.value = formattedValue;
    
    // Call the original onChange with formatted value
    onChange(formattedValue);
    
    previousValue = formattedValue;
  };
};

/**
 * Phone number input props helper
 * @param value - Current phone value
 * @param onChange - Change handler
 * @returns Props object for phone input field
 */
export const getPhoneInputProps = (
  value: string,
  onChange: (value: string) => void
) => {
  const error = getPhoneValidationError(value);
  
  return {
    type: 'tel',
    value: value,
    onChange: createPhoneMaskHandler(onChange),
    placeholder: '(250) 555-0123',
    maxLength: 14, // (XXX) XXX-XXXX
    className: `form-input ${error ? 'border-red-500 focus:ring-red-500' : ''}`,
    'aria-invalid': !!error,
    'aria-describedby': error ? 'phone-error' : undefined
  };
};